/**
 * Pure-function business logic for feature flags + permissions.
 *
 * The `useFeatures` hook used to mix three concerns: data fetching,
 * permission checks, and toggle mutations. This module extracts the
 * pure-function logic so it can be unit-tested without React, Supabase
 * or React Query.
 *
 * The hook in `src/hooks/useFeatures.ts` continues to be the public
 * surface; it now delegates the pure parts to these helpers.
 */

import {
  FEATURE_META,
  FEATURE_PLANS,
  FREIE_FUNKTIONEN,
  type Feature,
  type ModulTyp,
  type FeatureMeta,
} from '@/hooks/useFeatures.constants';

/**
 * Returns `true` when every dependency declared in `FEATURE_META[feature].requires`
 * is present in the supplied list of enabled features.
 *
 * Example:
 *   canEnable('rezeptanalyse', ['ernaehrungsplaene'])  → true
 *   canEnable('rezeptanalyse', [])                     → false
 */
export function canEnableFeature(feature: Feature, enabled: readonly Feature[]): boolean {
  const meta = findFeatureMeta(feature);
  if (!meta?.requires || meta.requires.length === 0) return true;
  return meta.requires.every((dep) => enabled.includes(dep));
}

/**
 * Returns the module tier (`basis` | `praxis` | `klinik` | `frei`) for
 * a given feature, or `'frei'` if the feature is unknown.
 */
export function getFeatureModulTyp(feature: Feature): ModulTyp {
  return findFeatureMeta(feature)?.modulTyp ?? 'frei';
}

/**
 * Returns `true` when an admin is allowed to toggle this feature themselves.
 *
 * - Regular org admins: only the "Freie Funktionen" tier is self-toggleable;
 *   basis/praxis/klinik features are part of the customer's package and managed
 *   by Icarus.
 * - Icarus superadmins: can toggle any feature for any org (cross-mode override),
 *   e.g. enabling klinik-features on a praxis org at a customer's request.
 */
export function isFeatureToggleable(
  feature: Feature,
  options: { isAdmin: boolean; isSuperAdmin?: boolean },
): boolean {
  if (!options.isAdmin) return false;
  if (options.isSuperAdmin) return true;
  return FREIE_FUNKTIONEN.includes(feature);
}

/**
 * Compute the next feature list after toggling one entry. Pure: returns
 * a new array, never mutates the input.
 */
export function toggleFeature(
  feature: Feature,
  enabled: readonly Feature[],
): Feature[] {
  return enabled.includes(feature)
    ? enabled.filter((f) => f !== feature)
    : [...enabled, feature];
}

/**
 * Returns the canonical feature list for a given plan, falling back to
 * `praxis` if the plan key is unknown.
 */
export function getPlanFeatures(plan: string): Feature[] {
  return [...(FEATURE_PLANS[plan] ?? FEATURE_PLANS.praxis)];
}

/**
 * Compute the feature list for switching an org onto a different paket
 * (Basis-Praxis ↔ Klinik) while preserving any "freie Funktionen"
 * add-ons the customer already pays for.
 *
 * Without this helper a naive plan switch would wipe every add-on —
 * e.g. a praxis with Telemedizin and Outcome-Tracking becomes a klinik
 * without those toggles. The admin "Workspace anpassen" UI (PR 1.4)
 * calls this so the paket radio only swaps the basis/praxis/klinik
 * tier and leaves the orthogonal add-ons untouched.
 *
 * Returns a deduplicated array. Pure: never mutates its inputs.
 */
export function switchToPlanFeatures(
  plan: string,
  current: readonly Feature[],
): Feature[] {
  const planFeatures = getPlanFeatures(plan);
  const preservedAddOns = current.filter((f) => FREIE_FUNKTIONEN.includes(f));
  // Use a Set-based dedupe to avoid the edge case where a feature is
  // both in the new plan and in the add-on list (shouldn't happen today
  // but cheap insurance against future metadata drift).
  return Array.from(new Set<Feature>([...planFeatures, ...preservedAddOns]));
}

/**
 * Compute the *added* and *removed* features when transitioning from
 * one plan to another. Useful for the admin "switch plan" flow that
 * needs to show the user a confirmation diff.
 */
export interface FeatureDiff {
  added: Feature[];
  removed: Feature[];
}

export function diffFeatures(
  before: readonly Feature[],
  after: readonly Feature[],
): FeatureDiff {
  return {
    added: after.filter((f) => !before.includes(f)),
    removed: before.filter((f) => !after.includes(f)),
  };
}

/** Internal helper — typed lookup against the metadata table. */
function findFeatureMeta(feature: Feature): FeatureMeta | undefined {
  return FEATURE_META.find((m) => m.key === feature);
}
